import { Box, Button, Collapse, Typography } from '@mui/material'
import { memo, useCallback, useState } from 'react'
import { IoCaretDown, IoCaretUp } from 'react-icons/io5'

import { QuestionStorePayload } from '@/types'

export interface QuestionCardOptionsPreviewProps {
  question: QuestionStorePayload
}

export const QuestionCardOptionsPreview = memo<QuestionCardOptionsPreviewProps>(
  ({ question }) => {
    const [expanded, setExpanded] = useState<boolean>(false)
    const [feedbackVisible, setFeedbackVisible] = useState<Record<number, boolean>>({})

    const handleToggle = useCallback(() => {
      setExpanded((prev) => !prev)
    }, [])

    const handleFeedback = useCallback((order: number) => {
      setFeedbackVisible((prevState) => ({
        ...prevState,
        [order]: !prevState[order],
      }))
    }, [])

    const getOptionLetter = useCallback(
      (order: string) => String.fromCharCode(96 + parseInt(order)) + ')',
      [],
    )

    const sortedOptions = question.options.slice().sort((a, b) => a.order - b.order)

    if (question.type !== 'multiple choice') return null

    return (
      <Box data-qa="question-card-options-preview">
        <Button
          onClick={handleToggle}
          variant="text"
          endIcon={expanded ? <IoCaretUp /> : <IoCaretDown />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
          }}>
          Ver opções
        </Button>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box mt={2}>
            {sortedOptions.length > 0 ? (
              sortedOptions.map((option) => (
                <Box
                  key={option.order}
                  data-qa={`option-container-${option.order}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: 2,
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: ({ palette }) =>
                      option.its_correct
                        ? palette.success.light + '20'
                        : palette.error.light + '20',
                    border: ({ palette }) =>
                      `1px solid ${
                        option.its_correct ? palette.success.light : palette.error.light
                      }`,
                  }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      component="div"
                      variant="body1"
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        fontWeight: 500,
                        mb: option.feedback ? 1 : 0,
                      }}>
                      <Typography
                        component="span"
                        sx={{ fontWeight: 700, minWidth: '24px' }}>
                        {getOptionLetter(option.order.toString())}
                      </Typography>
                      <Box dangerouslySetInnerHTML={{ __html: option.option }} />
                    </Typography>

                    {option.feedback && (
                      <>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleFeedback(option.order)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            p: 0.5,
                            minWidth: 'auto',
                          }}>
                          {feedbackVisible[option.order] ? 'Ocultar' : 'Ver'} feedback
                        </Button>
                        <Collapse
                          in={feedbackVisible[option.order] || false}
                          timeout="auto"
                          unmountOnExit>
                          <Box
                            sx={{
                              mt: 1,
                              p: 2,
                              borderRadius: 1,
                              backgroundColor: ({ palette }) => palette.info.light + '20',
                              border: ({ palette }) => `1px solid ${palette.info.light}`,
                            }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              dangerouslySetInnerHTML={{ __html: option.feedback }}
                            />
                          </Box>
                        </Collapse>
                      </>
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma opção encontrada
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    )
  },
)

QuestionCardOptionsPreview.displayName = 'QuestionCardOptionsPreview'
